import { Card, LinearProgress, Skeleton, Theme } from "@mui/material";
import * as React from "react";
import { FC, FunctionComponent } from "react";

import { SxProps } from "@mui/system";
import { useAppDispatch, useAppSelector } from "#store";
import { IoState, isDone } from "#store/types";
import {
    selectInterpretationsRawById,
    selectTableResultRawById,
} from "#selectors";
import { Optional } from "#types";
import {
    fetchInterpretationFromServer,
    fetchInterpretationTableFromServer,
} from "#store/interpretations";
import { Interpretation } from "#services/pilotService/types";
import { P } from "#/components/Common/headings";
import { QueryType } from "#store/tables";
import { TableCardContent } from "#pages/results/TableCardContent";

const root: SxProps<Theme> = {
    maxWidth: "100%",
    width: "100%",
    minWidth: "100%",
};

type InterpretationCardProps = {
    rank?: number;
    interpretationId: number;
    downloadCsv: (sql: string, dataset: string, rid: string) => void;
};

const CardSkeleton: FC<{ id: number }> = ({ id }) => (
    <>
        <Skeleton variant={"rectangular"} height={118} animation={"wave"}>
            <P>{id}</P>
        </Skeleton>
    </>
);

const InterpretationCard = ({
    interpretation,
    downloadCsv,
}: {
    rank?: number;
    interpretation: Interpretation;
    downloadCsv: (sql: string, dataset: string, rid: string) => void;
}) => {
    const dispatch = useAppDispatch();
    const optionalTableIoState = useAppSelector(state =>
        selectTableResultRawById(state, interpretation.table)
    );
    React.useEffect(() => {
        dispatch(fetchInterpretationTableFromServer(interpretation.id));
    }, [interpretation.id, dispatch]);

    if (!isDone(optionalTableIoState)) {
        return (
            <Card sx={root}>
                <Skeleton variant={"rectangular"} height={100} />
            </Card>
        );
    } else {
        const table = optionalTableIoState.payload;
        return (
            <TableCardContent
                table={table}
                interpretation={interpretation}
                downloadCsv={downloadCsv}
            />
        );
    }
};

const IoStateInterpretationCard = ({
    iostate,
    interpretationId,
    rank,
    downloadCsv,
}: {
    iostate: Optional<IoState<Interpretation>>;
    interpretationId: number;
    rank: number;
    downloadCsv: (sql: string, dataset: string, rid: string) => void;
}) => {
    if (!iostate) {
        return <LinearProgress variant={"indeterminate"} />;
    } else {
        switch (iostate.state) {
            case "done":
                return (
                    <InterpretationCard
                        interpretation={iostate.payload}
                        rank={rank}
                        downloadCsv={downloadCsv}
                    />
                );
            case "fetching":
                return <CardSkeleton id={interpretationId} />;
            case "error":
            default:
                return <P>todo Error on interpretation {interpretationId}</P>;
        }
    }
};

export const InterpretationCardContainer: FunctionComponent<
    InterpretationCardProps
> = ({ rank = 0, interpretationId, downloadCsv }) => {
    const dispatch = useAppDispatch();
    React.useEffect(() => {
        dispatch(fetchInterpretationFromServer(interpretationId));
    }, [interpretationId, dispatch]);

    const interpretation: Optional<IoState<Interpretation>> = useAppSelector(
        state => selectInterpretationsRawById(state, interpretationId)
    );

    return (
        <IoStateInterpretationCard
            iostate={interpretation}
            interpretationId={interpretationId}
            rank={rank}
            downloadCsv={downloadCsv}
        />
    );
};
